<template>
  <li class="nav-item nav-search d-flex flex-grow-1">
    <a
      v-tracker="'element:search'"
      href="javascript:void(0)"
      class="nav-link nav-link-search flex-grow-1"
      @click="openSearch"
    >
      <feather-icon
        icon="SearchIcon"
        size="21"
      />
    </a>

    <div
      class="search-input"
      :class="{ open: showSearchBar }"
    >
      <div class="search-input-icon">
        <feather-icon icon="SearchIcon" />
      </div>
      <b-form-input
        v-if="showSearchBar"
        ref="searchInput"
        v-model="searchQuery"
        placeholder="Search product"
        autofocus
        autocomplete="off"
        @focus="onFocusInput"
        @input="onInputChange"
        @keyup.up.prevent="moveSelection(-1)"
        @keyup.down.prevent="moveSelection(1)"
        @keyup.esc="closeSearch"
        @keyup.enter.prevent="onEnter"
        @blur.stop="onBlurInput"
      />
      <div
        class="search-input-close"
        @mousedown.prevent
        @click="closeSearch"
      >
        <feather-icon icon="XIcon" />
      </div>

      <div
        v-if="showSearchBar && showDropdown"
        class="search-suggestions"
        @mousedown.prevent
      >
        <div class="suggestion-group-title d-flex align-items-center justify-content-between">
          <span>{{ suggestionHeader }}</span>
          <button
            v-if="!trimmedQuery && recentSearches.length"
            type="button"
            class="clear-history-btn"
            @click="clearHistory"
          >
            Clear
          </button>
        </div>

        <ul class="suggestion-list">
          <li
            v-for="(item, index) in displayedSuggestions"
            :key="item.key"
            class="suggestion-group-suggestion suggestion-item d-flex align-items-center"
            :class="{ 'suggestion-current-selected': index === activeSuggestionIndex }"
            @mouseenter="activeSuggestionIndex = index"
            @click="selectSuggestion(item)"
          >
            <feather-icon :icon="item.icon" size="14" class="mr-50" />
            <span class="text-truncate">{{ item.label }}</span>
          </li>
        </ul>

        <div v-if="!displayedSuggestions.length" class="suggestion-empty">
          No suggestions
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'

const SEARCH_HISTORY_KEY = 'product_search_history'
const PRODUCT_SUGGESTIONS_KEY = 'product_search_products'
const MAX_HISTORY = 8
const MAX_SUGGESTIONS = 8

function normalizeClean(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function normalizeWords(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
}

function levenshteinDistance(a, b) {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length

  const row = Array(a.length + 1)
  for (let j = 0; j <= a.length; j++) row[j] = j

  for (let i = 1; i <= b.length; i++) {
    let prev = i
    for (let j = 1; j <= a.length; j++) {
      const val = b.charAt(i - 1) === a.charAt(j - 1) ? row[j - 1] : Math.min(row[j - 1] + 1, prev + 1, row[j] + 1)
      row[j - 1] = prev
      prev = val
    }
    row[a.length] = prev
  }
  return row[a.length]
}

function charOverlapRatio(queryStr, targetStr) {
  if (!queryStr || !targetStr) return 0
  const qMap = {}
  for (const c of queryStr) {
    qMap[c] = (qMap[c] || 0) + 1
  }
  let matched = 0
  for (const c of targetStr) {
    if (qMap[c] > 0) {
      matched++
      qMap[c]--
    }
  }
  return matched / queryStr.length
}

function getMatchScore(query, item) {
  const label = typeof item === 'string' ? item : String(item?.label || '')
  const kode = typeof item === 'string' ? '' : String(item?.kode || '')

  const rawQ = String(query || '').toLowerCase().trim()
  const rawL = label.toLowerCase().trim()
  const rawK = kode.toLowerCase().trim()

  if (!rawQ) return 0

  // 1. Exact raw match
  if (rawL === rawQ || rawK === rawQ) return 100

  // 2. Clean normalized match (ignores hyphens, spaces, dots, special chars)
  const cleanQ = normalizeClean(rawQ)
  const cleanL = normalizeClean(rawL)
  const cleanK = normalizeClean(rawK)

  if (!cleanQ) return 0

  if (cleanL === cleanQ || cleanK === cleanQ) return 95

  // 3. Clean starts with cleanQ (e.g. "sdwan" matches "sdwan direct")
  if (cleanL.startsWith(cleanQ) || cleanK.startsWith(cleanQ)) return 85

  // 4. Clean contains cleanQ (e.g. "sdwan" matches "managed sd-wan")
  if (cleanL.includes(cleanQ) || cleanK.includes(cleanQ)) return 75

  // 5. Token word match (all query words present in target words)
  const qWords = normalizeWords(rawQ)
  const targetWords = [...normalizeWords(rawL), ...normalizeWords(rawK)]
  if (qWords.length > 0) {
    const allTokensFound = qWords.every(qw =>
      targetWords.some(tw => tw.includes(qw) || qw.includes(tw))
    )
    if (allTokensFound) return 65
  }

  // 6. Fuzzy character & Levenshtein similarity matching for typos & random keywords (e.g. "gibla" -> "global", "sdwan" -> "sd-wan")
  if (cleanQ.length >= 3) {
    let maxSimilarity = 0
    let minDistance = Infinity

    // Check against full clean label
    const fullDist = levenshteinDistance(cleanQ, cleanL)
    const fullMaxLen = Math.max(cleanQ.length, cleanL.length)
    if (fullMaxLen > 0) {
      const sim = 1 - fullDist / fullMaxLen
      if (sim > maxSimilarity) maxSimilarity = sim
    }

    // Check against each word token in target label/kode
    for (const tw of targetWords) {
      if (!tw) continue
      const dist = levenshteinDistance(cleanQ, tw)
      if (dist < minDistance) minDistance = dist

      const maxLen = Math.max(cleanQ.length, tw.length)
      if (maxLen > 0) {
        const sim = 1 - dist / maxLen
        if (sim > maxSimilarity) maxSimilarity = sim
      }

      // Also check character overlap ratio
      const overlap = charOverlapRatio(cleanQ, tw)
      if (overlap >= 0.75 && cleanQ[0] === tw[0]) {
        if (maxSimilarity < 0.5) maxSimilarity = 0.5
      }
    }

    if (minDistance <= 3 || maxSimilarity >= 0.4) {
      return Math.round(30 + maxSimilarity * 30)
    }
  }

  return 0
}

export default {
  components: {
    BFormInput,
  },
  data() {
    return {
      showSearchBar: false,
      searchQuery: '',
      recentSearches: [],
      productSuggestionPool: [],
      activeSuggestionIndex: -1,
      keepDropdownOpen: false,
    }
  },
  computed: {
    trimmedQuery() {
      return String(this.searchQuery || '').trim()
    },
    historySuggestions() {
      if (this.trimmedQuery) {
        const q = this.trimmedQuery
        const scored = this.recentSearches
          .map(item => ({
            item,
            score: getMatchScore(q, item),
          }))
          .filter(entry => entry.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, MAX_SUGGESTIONS)

        return scored.map(({ item }) => ({
          key: `h-${item}`,
          label: item,
          value: item,
          icon: 'ClockIcon',
        }))
      }
      return this.recentSearches.slice(0, MAX_SUGGESTIONS).map(item => ({
        key: `h-${item}`,
        label: item,
        value: item,
        icon: 'ClockIcon',
      }))
    },
    productSuggestions() {
      if (!this.trimmedQuery) return []
      const q = this.trimmedQuery

      const scored = this.productSuggestionPool
        .map(item => ({
          item,
          score: getMatchScore(q, item),
        }))
        .filter(entry => entry.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_SUGGESTIONS)

      return scored.map(({ item }) => ({
        key: `p-${item.kode || item.label}`,
        label: item.label,
        value: item.label,
        icon: 'BoxIcon',
      }))
    },
    displayedSuggestions() {
      if (!this.trimmedQuery) return this.historySuggestions
      return this.productSuggestions.length
        ? this.productSuggestions
        : this.historySuggestions
    },
    suggestionHeader() {
      if (!this.trimmedQuery) return 'Recent Searches'
      return this.productSuggestions.length
        ? 'Product Suggestions'
        : 'Recent Searches'
    },
    showDropdown() {
      return this.keepDropdownOpen && (this.displayedSuggestions.length > 0 || !this.trimmedQuery)
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (typeof to?.query?.s === 'string' && to.query.s.trim()) {
          this.searchQuery = to.query.s.trim()
          this.showSearchBar = true
        } else if (to?.name === 'product-search') {
          this.searchQuery = ''
          this.showSearchBar = true
        } else {
          this.showSearchBar = false
          this.searchQuery = ''
        }
      },
    },
    displayedSuggestions() {
      this.activeSuggestionIndex = -1
    },
    searchQuery(val) {
      this.toggleOverlay(Boolean(val || this.showDropdown))
    },
  },
  mounted() {
    this.loadStorageData()
    this.fetchProductSuggestions()
    if (this.$route?.name === 'product-search' && this.$route?.query?.s) {
      this.searchQuery = String(this.$route.query.s || '').trim()
      this.showSearchBar = true
    }
  },
  methods: {
    fetchProductSuggestions() {
      if (this.productSuggestionPool && this.productSuggestionPool.length >= 20) return

      const processRecords = (records) => {
        if (Array.isArray(records) && records.length) {
          const pool = records
            .map(item => ({
              label: String(item.PRODUCT || item.label || item.NAME || item.name || '').trim(),
              kode: String(item.KODE || item.kode || item.CODE || item.code || '').trim(),
            }))
            .filter(item => item.label)

          if (pool.length) {
            this.productSuggestionPool = pool
            try {
              localStorage.setItem(PRODUCT_SUGGESTIONS_KEY, JSON.stringify(pool))
            } catch (e) {}
            return true
          }
        }
        return false
      }

      const mapRequest = {
        draw: 1,
        length: 300,
        start: 0,
        search: { value: '', regex: false },
        columns: [
          { data: 'PRODUCT', name: '', searchable: true, orderable: true, search: { value: '', regex: false } },
        ],
        map: { KODE: 's', PRODUCT: 's' }
      }

      this.$http
        .post(
          '/product/browse/take/300/skip/0/orderBy.PRODUCT/asc/published/true',
          mapRequest,
          { skipGlobalErrorNotif: true }
        )
        .then(res => {
          const records =
            res?.data?.data?.records ||
            res?.data?.records ||
            res?.data?.data ||
            []
          if (!processRecords(records)) {
            this.fetchProductSuggestionsIndex()
          }
        })
        .catch(() => {
          this.fetchProductSuggestionsIndex()
        })
    },
    fetchProductSuggestionsIndex() {
      this.$http
        .get('/product/index', { skipGlobalErrorNotif: true })
        .then(res => {
          const records = res?.data?.data || res?.data?.records || res?.data || []
          if (Array.isArray(records) && records.length) {
            const pool = records
              .map(item => ({
                label: String(item.PRODUCT || item.label || item.NAME || item.name || '').trim(),
                kode: String(item.KODE || item.kode || item.CODE || item.code || '').trim(),
              }))
              .filter(item => item.label)

            if (pool.length) {
              this.productSuggestionPool = pool
              try {
                localStorage.setItem(PRODUCT_SUGGESTIONS_KEY, JSON.stringify(pool))
              } catch (e) {}
            }
          }
        })
        .catch(() => {})
    },
    loadStorageData() {
      try {
        const rawHistory = localStorage.getItem(SEARCH_HISTORY_KEY)
        const parsedHistory = rawHistory ? JSON.parse(rawHistory) : []
        this.recentSearches = Array.isArray(parsedHistory)
          ? parsedHistory.filter(Boolean).slice(0, MAX_HISTORY)
          : []
      } catch (e) {
        this.recentSearches = []
      }

      try {
        const rawProducts = localStorage.getItem(PRODUCT_SUGGESTIONS_KEY)
        const parsedProducts = rawProducts ? JSON.parse(rawProducts) : []
        this.productSuggestionPool = Array.isArray(parsedProducts)
          ? parsedProducts
              .filter(item => item && item.label)
              .map(item => ({
                label: String(item.label),
                kode: item.kode || '',
              }))
          : []
      } catch (e) {
        this.productSuggestionPool = []
      }
    },
    openSearch() {
      this.showSearchBar = true
      this.keepDropdownOpen = true
      if (typeof this.$route?.query?.s === 'string' && this.$route.query.s.trim()) {
        this.searchQuery = this.$route.query.s.trim()
      }
      this.loadStorageData()
      this.fetchProductSuggestions()
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focus()
      })
    },
    closeSearch() {
      this.keepDropdownOpen = false
      this.activeSuggestionIndex = -1
      this.toggleOverlay(false)
      this.searchQuery = ''
      this.showSearchBar = false

      if (this.$route?.name !== 'home') {
        this.$router.push({ name: 'home' }).catch(() => {})
      }
    },
    onFocusInput() {
      this.keepDropdownOpen = true
      this.loadStorageData()
      this.fetchProductSuggestions()
    },
    onBlurInput() {
      setTimeout(() => {
        this.keepDropdownOpen = false
        this.toggleOverlay(false)
        if (this.$route?.name !== 'product-search') {
          this.showSearchBar = false
          this.searchQuery = ''
        }
      }, 150)
    },
    onInputChange() {
      this.keepDropdownOpen = true
      this.activeSuggestionIndex = -1
    },
    moveSelection(direction) {
      if (!this.displayedSuggestions.length) return
      const max = this.displayedSuggestions.length - 1
      let next = this.activeSuggestionIndex + direction
      if (next < 0) next = max
      if (next > max) next = 0
      this.activeSuggestionIndex = next
    },
    onEnter() {
      if (
        this.activeSuggestionIndex >= 0 &&
        this.activeSuggestionIndex < this.displayedSuggestions.length
      ) {
        this.selectSuggestion(this.displayedSuggestions[this.activeSuggestionIndex])
        return
      }
      this.search()
    },
    selectSuggestion(item) {
      this.searchQuery = item?.value || item?.label || ''
      this.search()
    },
    saveSearchHistory(keyword) {
      const normalized = String(keyword || '').trim()
      if (!normalized) return
      const next = [
        normalized,
        ...this.recentSearches.filter(
          item => item.toLowerCase() !== normalized.toLowerCase()
        ),
      ].slice(0, MAX_HISTORY)
      this.recentSearches = next
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(next))
    },
    clearHistory() {
      this.recentSearches = []
      localStorage.removeItem(SEARCH_HISTORY_KEY)
    },
    toggleOverlay(visible) {
      if (this.$store && this.$store.commit) {
        this.$store.commit('app/TOGGLE_OVERLAY', Boolean(visible))
      }
    },
    search() {
      const keyword = String(this.searchQuery || '').trim()
      if (!keyword) return
      this.saveSearchHistory(keyword)
      this.keepDropdownOpen = false
      this.toggleOverlay(false)
      this.showSearchBar = true
      this.$router.push({ name: 'product-search', query: { s: keyword } }).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
p {
  margin: 0;
}

.search-input {
  position: relative;
}

.search-suggestions {
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  right: 0;
  z-index: 30;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
  max-height: 320px;
  overflow: auto;
}

.suggestion-group-title {
  font-weight: 600;
  padding: 0.75rem 0.95rem 0.4rem;
  color: #475569;
}

.clear-history-btn {
  border: none;
  background: transparent;
  color: #dc2626;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0;
  line-height: 1;
  cursor: pointer;
}

.suggestion-group-suggestion {
  padding: 0.6rem 0.95rem;
}

.suggestion-item {
  cursor: pointer;
}

.suggestion-item:hover {
  background-color: rgba(148, 163, 184, 0.14);
}

.suggestion-empty {
  padding: 0.75rem 0.95rem;
  color: #94a3b8;
  font-size: 0.84rem;
}

.suggestion-current-selected {
  background-color: $body-bg;

  .dark-layout & {
    background-color: $theme-dark-body-bg;
  }
}
</style>
